/**
 * One legal source: its versions, and how they got here.
 *
 * A version is immutable and a re-fetch of unchanged bytes creates none, so this
 * screen is mostly a record rather than a workspace. What it has to make obvious:
 *
 *   which version is CURRENT, and what its digest is - the identity a citation is
 *   accountable to;
 *
 *   what the extractor actually read. A ministry PDF with a two-column layout or
 *   a table can extract into nonsense, and the only way anybody finds out before
 *   a release is by looking at the text;
 *
 *   what happened on the last fetches, including the failures. A 404 on a
 *   guideline URL means the ministry moved the file, and that is a fact about the
 *   corpus, not a transient error to swallow.
 */
import { useState, type ReactNode } from 'react';
import { Link, useLoaderData, useRevalidator } from 'react-router';
import {
  ApiError,
  del,
  get,
  post,
  postFile,
  type LegalDocument,
  type LegalSourceDetail,
} from '../api.ts';
import { Card, Empty, Pill, Stat, Table, jaDate, jaNumber, rowOpen } from '../components/ui.tsx';
import { ModalForm } from '../components/Modal.tsx';

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'muted'> = {
  READY: 'ok',
  FETCHING: 'warn',
  FAILED: 'danger',
  SUPERSEDED: 'muted',
};

const STATUS_LABEL: Record<string, string> = {
  READY: '現行',
  FETCHING: '取得中',
  FAILED: '失敗',
  SUPERSEDED: '旧版',
};

const JOB_LABEL: Record<string, string> = {
  QUEUED: '待機中',
  RUNNING: '取得中',
  DONE: '完了',
  FAILED: '失敗',
};

const KIND_LABEL: Record<string, string> = {
  law: '法令',
  guideline: 'ガイドライン',
  notice: '通知',
  internal: '社内資料',
};

interface VersionDiff {
  current: number;
  previous: number | null;
  added: { headingPath: string; text: string }[];
  removed: { headingPath: string; text: string }[];
  unchanged: number;
}

/** One labelled fact, in the same list shape the clinic and user screens use. */
function Fact({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="factgrid__row">
      <dt>{label}</dt>
      <dd>{children || <span className="muted">—</span>}</dd>
      {hint && <dd className="muted small">{hint}</dd>}
    </div>
  );
}

export default function AdminLegalSource() {
  const { source, documents, jobs } = useLoaderData<LegalSourceDetail>();
  const revalidator = useRevalidator();

  const [url, setUrl] = useState(source.canonical_url ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [text, setText] = useState<{ id: string; body: string } | null>(null);
  /** The version about to be retired, and the one about to be deleted. */
  const [retiring, setRetiring] = useState<LegalDocument | null>(null);
  const [removing, setRemoving] = useState<LegalDocument | null>(null);
  /** What changed between a version and the one before it. */
  const [diff, setDiff] = useState<{ version: number; body: VersionDiff } | null>(null);

  const current = documents.find((document) => document.status === 'READY') ?? null;

  const report = (err: unknown, fallback: string) =>
    setError(err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : fallback);

  /**
   * Queue a fetch, then wait for the worker to claim it.
   *
   * Polled rather than pushed: the queue is drained every few seconds and a
   * fetch takes as long as a ministry's server takes. The alternative - holding
   * the request open - is what makes a browser time out having created something
   * the operator cannot see.
   */
  async function fetchNow() {
    setBusy('fetch');
    setError(null);
    setNotice(null);
    try {
      const job = await post<{ id: string }>(`/admin/legal/sources/${source.id}/fetch`, { url });
      for (let attempt = 0; attempt < 30; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const current = await get<{ status: string; unchanged: boolean; error: string | null }>(
          `/admin/legal/fetch-jobs/${job.id}`,
        );
        if (current.status === 'DONE') {
          setNotice(
            current.unchanged
              ? '内容に変更はありませんでした。新しい版は作成していません。'
              : '新しい版を取り込みました。内容を確認してからリリースを作成してください。',
          );
          break;
        }
        if (current.status === 'FAILED') {
          setError(current.error ?? '取得に失敗しました');
          break;
        }
      }
      await revalidator.revalidate();
    } catch (err) {
      report(err, '取得を開始できませんでした');
    } finally {
      setBusy(null);
    }
  }

  async function upload(file: File) {
    setBusy('upload');
    setError(null);
    setNotice(null);
    try {
      const stored = await postFile<{ unchanged: boolean; version_no: number }>(
        `/admin/legal/sources/${source.id}/documents`,
        file,
      );
      setNotice(
        stored.unchanged
          ? '同じ内容の版がすでにあります。新しい版は作成していません。'
          : `第${stored.version_no}版として取り込みました。`,
      );
      await revalidator.revalidate();
    } catch (err) {
      report(err, '取り込めませんでした');
    } finally {
      setBusy(null);
    }
  }

  /**
   * What this version changed.
   *
   * The question a re-fetch provokes is not "is it different" - a new version
   * exists only because the bytes were - but WHICH provisions moved. A ministry
   * re-issue is a paragraph in two hundred, and reading the whole document again
   * to find it is how a release stops being reviewed.
   */
  async function showDiff(document: LegalDocument) {
    if (diff?.version === document.version_no) return setDiff(null);
    setBusy(`diff-${document.id}`);
    try {
      const body = await get<VersionDiff>(`/admin/legal/documents/${document.id}/diff`);
      setDiff({ version: document.version_no, body });
    } catch (err) {
      report(err, '差分を取得できませんでした');
    } finally {
      setBusy(null);
    }
  }

  async function showText(document: LegalDocument) {
    if (text?.id === document.id) return setText(null);
    setBusy(`text-${document.id}`);
    try {
      const full = await get<{ extracted_text: string }>(`/admin/legal/documents/${document.id}`);
      setText({ id: document.id, body: full.extracted_text });
    } catch (err) {
      report(err, '本文を取得できませんでした');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Card
        title={source.title}
        caption={source.publisher}
        actions={
          <Link className="btn btn--sm btn--ghost" to="/admin/legal">
            一覧に戻る
          </Link>
        }
      >
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        {notice && !error && (
          <p className="muted small" role="status">
            {notice}
          </p>
        )}

        {/*
          The four figures the page exists to answer - what is current, how
          fresh it is, how much text the corpus actually got - on the same deep
          tile every other detail screen leads with.
        */}
        <div className="grid grid--4">
          <Stat label="区分" value={KIND_LABEL[source.kind] ?? source.kind} small />
          <Stat label="現行版" value={current ? `第${current.version_no}版` : '未取り込み'} small />
          <Stat label="最終取得" value={current ? jaDate(current.fetched_at) : '—'} small />
          <Stat
            label="本文の長さ"
            value={current ? `${jaNumber(current.text_length)} 字` : '—'}
            small
          />
        </div>

        <dl className="factgrid">
          <Fact
            label="正規URL"
            hint="発行元の公開ページ。チェック結果の出典として表示され、原典をそのまま開けます。"
          >
            {source.canonical_url ? (
              <a href={source.canonical_url} target="_blank" rel="noreferrer noopener">
                {source.canonical_url}
              </a>
            ) : null}
          </Fact>
          <Fact
            label="ダイジェスト"
            hint="原本のSHA-256。同じ値なら同じ文書として扱い、新しい版は作りません。"
          >
            {current ? <code className="small">{current.sha256.slice(0, 16)}…</code> : null}
          </Fact>
          <Fact label="版の数">{`${jaNumber(documents.length)} 版`}</Fact>
          <Fact label="登録日">{jaDate(source.created_at)}</Fact>
          <Fact label="メモ">{source.notes}</Fact>
        </dl>
      </Card>

      <Card
        title="本文を取り込む"
        caption="URLからの取得はワーカーが実行し、リダイレクト先も検証されます。"
      >
        <div className="tenantbar">
          <div className="field" style={{ marginBottom: 0, minWidth: 320, flex: 1 }}>
            <label htmlFor="src-url">URLから取得</label>
            <input
              id="src-url"
              type="url"
              value={url}
              maxLength={500}
              placeholder="https://www.mhlw.go.jp/..."
              onChange={(event) => setUrl(event.target.value)}
            />
            <span className="field__hint">許可されたホストのhttps URLのみ。</span>
          </div>
          <button
            className="btn btn--sm"
            type="button"
            style={{ alignSelf: 'start', marginTop: 22 }}
            disabled={busy !== null || url.trim() === ''}
            onClick={() => void fetchNow()}
          >
            {busy === 'fetch' ? '取得中…' : '取得する'}
          </button>

          <div className="field" style={{ marginBottom: 0, minWidth: 260 }}>
            <label htmlFor="src-file">ファイルをアップロード</label>
            <input
              id="src-file"
              type="file"
              accept="application/pdf,text/html,text/plain"
              disabled={busy !== null}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload(file);
                event.target.value = '';
              }}
            />
            <span className="field__hint">
              PDF・HTML・テキスト。公開URLのない通知などに使います。
            </span>
          </div>
        </div>
      </Card>

      <Card title="版の履歴" caption="版は書き換えません。内容が変われば新しい版になります。">
        {documents.length === 0 ? (
          <Empty>まだ取り込まれていません。</Empty>
        ) : (
          <>
            <Table
              head={
                <>
                  <th scope="col">版</th>
                  <th scope="col">状態</th>
                  <th scope="col">取得元</th>
                  <th scope="col" className="num">
                    文字数
                  </th>
                  <th scope="col">取得日時</th>
                  <th scope="col" />
                </>
              }
            >
              {documents.map((document) => (
                // Opening a version means reading what was extracted from it,
                // which is the whole reason to look at one.
                <tr key={document.id} {...rowOpen(() => void showText(document))}>
                  <td>第{document.version_no}版</td>
                  <td>
                    <Pill tone={STATUS_TONE[document.status] ?? 'muted'}>
                      {STATUS_LABEL[document.status] ?? document.status}
                    </Pill>
                  </td>
                  <td className="muted small">
                    {document.origin === 'url' ? (document.origin_url ?? 'URL') : 'アップロード'}
                    <span style={{ display: 'block' }}>
                      {document.content_type}・{jaNumber(document.byte_size)} バイト・
                      {document.parser_version}
                    </span>
                  </td>
                  <td className="num">{jaNumber(document.text_length)}</td>
                  <td>{jaDate(document.fetched_at)}</td>
                  <td>
                    <div className="rowactions">
                      <button
                        className="btn btn--ghost btn--sm"
                        type="button"
                        disabled={busy !== null}
                        onClick={() => void showText(document)}
                      >
                        {text?.id === document.id ? '閉じる' : '本文を確認'}
                      </button>
                      {/*
                        Retiring and deleting are different acts. A superseded
                        version stays citable by the releases that already
                        contain it - which is what a finding made last month
                        needs - while a delete is for a failed fetch or a file
                        taken by mistake, and the server refuses it once a
                        release depends on the version.
                      */}
                      {/*
                        Only from the second version on: the first has nothing
                        to be compared against, and a control that always
                        answers "nothing to show" teaches people not to press
                        it.
                      */}
                      {document.version_no > 1 && (
                        <button
                          className="btn btn--ghost btn--sm"
                          type="button"
                          disabled={busy !== null}
                          onClick={() => void showDiff(document)}
                        >
                          {diff?.version === document.version_no ? '差分を閉じる' : '差分'}
                        </button>
                      )}
                      {document.status !== 'SUPERSEDED' && (
                        <button
                          className="btn btn--ghost btn--sm"
                          type="button"
                          onClick={() => setRetiring(document)}
                        >
                          旧版にする
                        </button>
                      )}
                      <button
                        className="btn btn--ghost btn--sm"
                        type="button"
                        onClick={() => setRemoving(document)}
                      >
                        削除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </Table>

            {diff && (
              <>
                <p className="form__section">
                  第{diff.body.current}版の変更点
                  {diff.body.previous !== null && `（第${diff.body.previous}版との比較）`}
                </p>
                <p className="muted small" style={{ marginTop: 0 }}>
                  条文の単位（チャンク）で比較しています。
                  変更なし {jaNumber(diff.body.unchanged)}・追加 {jaNumber(diff.body.added.length)}・
                  削除 {jaNumber(diff.body.removed.length)}。
                </p>
                {diff.body.added.length === 0 && diff.body.removed.length === 0 ? (
                  <Empty>本文の変更はありませんでした（体裁のみの更新と考えられます）。</Empty>
                ) : (
                  <ul className="feed">
                    {diff.body.added.map((piece, at) => (
                      <li key={`add-${at}`}>
                        <span className="feed__dot" style={{ background: 'var(--ok)' }} />
                        <span>
                          <Pill tone="ok">追加</Pill> {piece.headingPath || '（見出しなし）'}
                          <span className="muted small" style={{ display: 'block' }}>
                            {piece.text.replace(/\s+/g, ' ').slice(0, 160)}
                          </span>
                        </span>
                      </li>
                    ))}
                    {diff.body.removed.map((piece, at) => (
                      <li key={`del-${at}`}>
                        <span className="feed__dot" style={{ background: 'var(--danger)' }} />
                        <span>
                          <Pill tone="danger">削除</Pill> {piece.headingPath || '（見出しなし）'}
                          <span className="muted small" style={{ display: 'block' }}>
                            {piece.text.replace(/\s+/g, ' ').slice(0, 160)}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}

            {text && (
              <>
                <p className="form__section">抽出されたテキスト</p>
                <p className="muted small" style={{ marginTop: 0 }}>
                  これはモデルへの指示ではなく参照データとして扱われます。表や段組のPDFでは
                  抽出が乱れることがあるため、リリース前にここで確認してください。
                </p>
                <pre className="pre">{text.body.slice(0, 20_000)}</pre>
              </>
            )}
          </>
        )}
      </Card>

      <Card title="取得の履歴">
        {jobs.length === 0 ? (
          <Empty>URLからの取得はまだ行われていません。</Empty>
        ) : (
          <ul className="feed">
            {jobs.map((job) => (
              <li key={job.id}>
                <span
                  className="feed__dot"
                  style={job.status === 'FAILED' ? { background: 'var(--danger)' } : undefined}
                />
                <span>
                  <Pill tone={job.status === 'FAILED' ? 'danger' : 'muted'}>
                    {JOB_LABEL[job.status] ?? job.status}
                  </Pill>{' '}
                  {job.unchanged ? '内容に変更なし' : (job.error ?? job.url)}
                  <span className="muted small" style={{ display: 'block' }}>
                    {job.url}
                  </span>
                </span>
                <span className="feed__time">{jaDate(job.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {retiring && (
        <ModalForm
          open
          onClose={() => setRetiring(null)}
          title="この版を旧版にしますか"
          caption={`第${retiring.version_no}版：新しいリリースには使われなくなります。`}
          submitLabel="旧版にする"
          onSubmit={async () => {
            await post(`/admin/legal/documents/${retiring.id}/supersede`, {});
            setRetiring(null);
          }}
        >
          <p className="field--full" style={{ margin: 0 }}>
            すでに有効化されたリリースに含まれている場合、そのリリースからは引き続き参照されます。
            記録は残したまま、現行として扱うのをやめるための操作です。
          </p>
        </ModalForm>
      )}

      {removing && (
        <ModalForm
          open
          onClose={() => setRemoving(null)}
          title="この版を削除しますか"
          caption={`第${removing.version_no}版：本文と抽出結果が消えます。`}
          submitLabel="削除する"
          danger
          onSubmit={async () => {
            await del(`/admin/legal/documents/${removing.id}`);
            setRemoving(null);
          }}
        >
          <p className="field--full" style={{ margin: 0 }}>
            リリースに含まれている版は削除できません。その場合は「旧版にする」を使ってください。
          </p>
        </ModalForm>
      )}
    </>
  );
}
